import { track } from 'mex_components/app/scripts/tracking/newrelic';
import addClickHandler from 'mex_components/app/scripts/events/addClickHandler';
import addToCartTracking from './add_to_cart_tracking';
import addEnhancedDataToCartTracking
 from '../../app/scripts/tracking/add_enhanced_data_to_cart_tracking';
import cartRequest from 'mex_components/app/scripts/cart/cartRequest';
import fetchCart from 'mex_components/app/scripts/cart/fetchCart';
import forEachSelector from 'mex_components/app/scripts/elements/forEachSelector';
import trackCartError from 'mex_components/app/scripts/cart/trackCartError';

/**
 * Adds the item to the visitor or client's cart and redirects
 * the user to their cart page, if successful
 *
 * @param {Event} event Current click event
 */
export const handleAddToCartClick = async (event) => {
  const target = event.target;
  const cartId = await _getCartId();
  const cookies = document.cookie || {};
  const details = 'data: { '
                  + `event: ${event}, `
                  + `windowCart: ${window.vt?.cart}, `
                  + `cookies: ${cookies} }`;
  const lineItemId = target.dataset?.offer;
  if (!cartId) {
    const errorMsg = 'Cart ID missing, skipping add to cart at /v1/carts/<id>/line-items';
    trackCartError(new Error(errorMsg), details);
    return;
  } else if (!lineItemId) {
    const errorMsg = 'Line Item ID missing, skipping add to cart at /v1/carts/<id>/line-items';
    trackCartError(new Error(errorMsg), details);
    return;
  } else {
    const url = `https://${process.env.API_HOST}/v1/carts/${cartId}/line-items`;
    const data = {
      type: 'line_items',
      attributes: {
        external_id: lineItemId
      }
    };

    // storing the price as shown on the CDP after verifying with VTSA, although
    // "original_price_cents" implies the price before subscriptions
    const priceCents = parseInt(target.dataset.priceCents);
    if (priceCents >= 0) {
      data.attributes.original_price_cents = priceCents;
    }

    // adds cart data to data layer for GTM
    addToCartTracking(data);
    addEnhancedDataToCartTracking(data);

    return await cartRequest(url, data)
      .then((json) => {
        track('Cart success: Item added', { id: json?.data?.id });

        // direct visitor to their cart page
        window.location.href = '/cart';
      })
      .catch((error) => {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'js-server-error-message';
        errorMessage.innerHTML = 'Add to cart failed';
        errorMessage.style.color = '#ED6C72';
        errorMessage.style.fontWeight = 'bold';
        errorMessage.style.marginTop = '10px';
        
        document.querySelectorAll('.js-server-error-message').forEach(el => el.remove());
        target.parentNode.insertBefore(errorMessage, target.nextSibling);

        const details = 'data: { '
                        + 'message: Add to cart failed, '
                        + `error: ${error}, `
                        + `cartId: ${cartId}, `
                        + `windowCart: ${window.vt?.cart}, `
                        + `cookies: ${cookies} }`;
        trackCartError(error, details);
      });
    }
};

/**
 * Gets the checkout cart id from the window variable, or tries to refetch the cart.
 * Will log a NR notice error if the id is missing before and/or after the refetch.
 *
 * @returns cartId
 */
const _getCartId = async () => {
  // return window cart variable if it already exists
  let cartId = window?.vt?.cart?.id;
  if (cartId) {
    return cartId;
  }

  // otherwise, log an error and try to re-fetch it
  let errorMsg = 'Cart ID from window variable missing; retrying';
  trackCartError(new Error(errorMsg), errorMsg);

  // refetch cart which should set window variable
  await fetchCart();
  cartId = window?.vt?.cart?.id;
  if (cartId) {
    return cartId;
  }

  // if there's still no cart id, log another error and continue
  errorMsg = 'Cart ID from window variable missing after retry';
  trackCartError(new Error(errorMsg), errorMsg);
};

export default function setup() {
  setupEventHandlers();
}

const setupEventHandlers = () => {
  forEachSelector('.js-add-to-cart', (el) => {
    addClickHandler(el, handleAddToCartClick);
  });
};
